import {createSearchParams, useParams} from 'react-router-dom'
import {deleteRequest, getRequest} from '../../../services/base'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {CircuitContext, SET_CIRCUIT_DATA, SET_SELECTED_CIRCUIT_DATA,} from './context/context'
import Button from '../../common/Button'
import InputText from '../../common/InputText'
import CircuitTable from './CircuitTable'
import CircuitFormModal from './form'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Flex, Form, Modal, Row, Select, Space} from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, getDeleteTargetPage} from '../../../utils/common'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const CircuitList = () => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(CircuitContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [signalFilter, setSignalFilter] = useState(null)
  const [signalOptions, setSignalOptions] = useState([])
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(`/api/circuit/?vehicle=${vehicle}`)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  const getCircuitListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'circuits.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_CIRCUIT_DATA, data: res })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'circuits.loadError' }))
    }
  }

  const fetchSignalOptions = async (search = '') => {
    let res = await getRequest(
      `/api/signal/options/?vehicle=${vehicle}&search=${search}&limit=50`
    )
    // Handle object response with results/data arrays. `results` is now always
    // present (empty on failure), so branch on the status rather than on its
    // truthiness - otherwise the res.data fallback below becomes unreachable.
    if (res?.status !== false && (res?.results?.length || res?.data)) {
      const items = res.results?.length ? res.results : res.data || []
      setSignalOptions(
        items.map((o) => ({
          value: o,
          label: o,
        }))
      )
      return
    }
    setSignalOptions([])
  }

  const buildUrl = () => {
    const params = new URLSearchParams({ vehicle })
    if (search) params.append('search', search)
    if (signalFilter) params.append('signal_name', signalFilter)
    return `/api/circuit/?${params.toString()}`
  }

  useEffect(() => {
    fetchSignalOptions()
    getCircuitListData()

    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.circuit' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
  }, [])

  useEffect(() => {
    currentUrl.current = buildUrl()
    getCircuitListData()
  }, [debouncedSearch, signalFilter])

  const onEdit = (record) => {
    dispatch({
      type: SET_SELECTED_CIRCUIT_DATA,
      data: { ...record, index: state.data?.results?.indexOf(record) },
    })
  }

  const onCreateCircuit = () => {
    dispatch({
      type: SET_SELECTED_CIRCUIT_DATA,
      data: {},
    })
  }

  const deleteCircuit = async (record) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'circuits.deleting' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await deleteRequest(
      `/api/circuit/${record.id}/?vehicle=${vehicle}`
    )
    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })
    if (res?.status !== false) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'circuits.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getCircuitListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'circuits.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'circuits.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'circuits.confirmDeleteContent' },
        { name: record.name }
      ),
      onOk: () => deleteCircuit(record),
      centered: true,
      maskClosable: true,
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.data?.results?.length) return []
    return state.data.results.map((item, index) => ({
      ...item,
      key: `circuit-${index}`,
      rowKey: `circuit-${item.id}`,
      index,
    }))
  }, [state.data])

  return (
    <>
      {contextHolder}

      <CircuitFormModal getCircuitListData={getCircuitListData} />

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex gap={16} justify="space-between" className="full-width">
              <Flex gap={16} align="center" className='full-width'>
                <InputText
                  name="search_circuit"
                  type="search"
                  placeholder={intl.formatMessage({
                    id: 'circuits.searchPlaceholder',
                  })}
                  value={search}
                  isRequired={false}
                  wrapperClass="search-circuit"
                  onChange={(value) => setSearch(value)}
                />

                <div className='select-signal-name'>
                  <Select
                    placeholder={intl.formatMessage({
                      id: 'circuits.signalName',
                    })}
                    options={signalOptions}
                    value={signalFilter}
                    onChange={(value) => setSignalFilter(value)}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                  />
                </div>
              </Flex>

              <Space size="middle" align="center">
                <Button onClick={onCreateCircuit}>
                  {intl.formatMessage({ id: 'circuits.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex vertical gap={16} style={{ width: '100%' }}>
            <CircuitTable
              dataSource={dataSource}
              pagination={pagination}
              getCircuitListData={getCircuitListData}
              onEdit={onEdit}
              onDelete={confirmDelete}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default CircuitList
